import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  LockNegotiationResultDto,
  LockSelectionResultDto,
  NegotiationAuthStateDto,
  NegotiationCandidatesDto,
  NegotiationClient,
  NegotiationComparisonDto,
  NegotiationLinesDto,
  NegotiationHistoryDto,
  NegotiationHistoryQueryInput,
  ReopenNegotiationInput,
  ReopenNegotiationResultDto,
} from '@/api/negotiation-client';
import { useNegotiationClient } from '@/api/negotiation-client-context';

// =============================================================================
// use-negotiation.ts — Hooks TanStack Query de la négociation ADMIN (S18 / stream B).
// =============================================================================
// Lectures (cohorte candidate + état NegotiationAuth) en useQuery ; commandes (réouverture / verrouillage) en
// useMutation (effets de bord serveur). Le client effectif vient du NegotiationClientProvider (doublable,
// stub-first).
//
// ZÉRO règle métier ici : l'autorité (garde per-profil profilesAuthorized, transitions de participation,
// verrouillage final) est l'API/worker. Après une RÉOUVERTURE ou un VERROUILLAGE, on INVALIDE l'état
// NegotiationAuth pour refléter l'état serveur. L'erreur API (NegotiationApiError) remonte telle quelle.

const NEGOTIATION_KEY = ['negotiation'] as const;

/** Clé de query de la cohorte candidate d'une campagne. */
export function candidatesKey(campaignId: string): readonly unknown[] {
  return [...NEGOTIATION_KEY, 'candidates', campaignId];
}

/** Clé de query de l'état NegotiationAuth d'une campagne. */
export function authStateKey(campaignId: string): readonly unknown[] {
  return [...NEGOTIATION_KEY, 'auth', campaignId];
}

/** Clé de query de la comparaison avant/après d'un fournisseur dans une campagne. */
export function comparisonKey(
  campaignId: string,
  supplierSiret: string,
): readonly unknown[] {
  return [...NEGOTIATION_KEY, 'comparison', campaignId, supplierSiret];
}

/**
 * LOT N1 — clé de query du read-model par LIGNE d'une campagne. DISTINCTE de `authStateKey` : les deux
 * surfaces cohabitent jusqu'au lot N4 (ajout pur, aucun écran modifié ici).
 */
export function linesKey(campaignId: string): readonly unknown[] {
  return [...NEGOTIATION_KEY, 'lines', campaignId];
}

/**
 * LOT N3 — clé de query de l'HISTORIQUE. Elle inclut le FILTRE et la PAGE : le filtrage et la pagination
 * sont serveur, deux onglets ou deux pages sont donc deux réponses DIFFÉRENTES et ne doivent jamais
 * partager une entrée de cache (sans quoi changer d'onglet afficherait la page de l'onglet précédent).
 */
export function historyKey(
  campaignId: string,
  query: NegotiationHistoryQueryInput = {},
): readonly unknown[] {
  return [
    ...NEGOTIATION_KEY,
    'history',
    campaignId,
    query.filter ?? 'Tous',
    query.page ?? 1,
    query.pageSize ?? 50,
  ];
}

/** Cohorte candidate à la réouverture (fournisseurs + profils éligibles) — borne la sélection. */
export function useNegotiationCandidates(
  campaignId: string,
): UseQueryResult<NegotiationCandidatesDto, Error> {
  const client: NegotiationClient = useNegotiationClient();
  return useQuery<NegotiationCandidatesDto, Error>({
    queryKey: candidatesKey(campaignId),
    queryFn: () => client.getCandidates(campaignId),
  });
}

/** État NegotiationAuth (autorisations de réouverture actives). */
export function useNegotiationAuthState(
  campaignId: string,
): UseQueryResult<NegotiationAuthStateDto, Error> {
  const client: NegotiationClient = useNegotiationClient();
  return useQuery<NegotiationAuthStateDto, Error>({
    queryKey: authStateKey(campaignId),
    queryFn: () => client.getAuthState(campaignId),
  });
}

/**
 * Comparaison avant/après TJM d'un fournisseur rouvert (A-NEGO §2.5). `enabled` borne le fetch au SEUL
 * fournisseur pertinent (déplié / ayant renégocié) — évite un appel inutile sur les autres.
 */
export function useNegotiationComparison(
  campaignId: string,
  supplierSiret: string,
  options: { readonly enabled: boolean },
): UseQueryResult<NegotiationComparisonDto, Error> {
  const client: NegotiationClient = useNegotiationClient();
  return useQuery<NegotiationComparisonDto, Error>({
    queryKey: comparisonKey(campaignId, supplierSiret),
    queryFn: () => client.getNegotiationComparison(campaignId, supplierSiret),
    enabled: options.enabled,
  });
}

/**
 * LOT N1 — LIGNES de négociation (grain Fournisseur × Profil × Séniorité), UN SEUL appel pour toute la
 * page. Remplace, à partir du lot N4, `useNegotiationAuthState` + `useNegotiationEffectiveModifDates`
 * (N+2 requêtes au montage). ZÉRO règle métier : écart, état, tri et valeurs viennent de l'API.
 */
export function useNegotiationLines(
  campaignId: string,
): UseQueryResult<NegotiationLinesDto, Error> {
  const client: NegotiationClient = useNegotiationClient();
  return useQuery<NegotiationLinesDto, Error>({
    queryKey: linesKey(campaignId),
    queryFn: () => client.getNegotiationLines(campaignId),
  });
}

/**
 * LOT N3 — HISTORIQUE des événements de NÉGOCIATION (colonne droite de la page), PAGINÉ et filtré par
 * onglet CÔTÉ SERVEUR. ZÉRO règle métier côté UI : le périmètre (« uniquement des éléments liés à la
 * négociation »), le tri, les compteurs et les montants viennent de l'API ; l'écran compose les libellés.
 */
export function useNegotiationHistory(
  campaignId: string,
  query: NegotiationHistoryQueryInput = {},
): UseQueryResult<NegotiationHistoryDto, Error> {
  const client: NegotiationClient = useNegotiationClient();
  return useQuery<NegotiationHistoryDto, Error>({
    queryKey: historyKey(campaignId, query),
    queryFn: () => client.getNegotiationHistory(campaignId, query),
  });
}

/** RÉOUVRIR ciblé (garde per-profil). Invalide l'état NegotiationAuth. */
export function useReopenNegotiation(
  campaignId: string,
): UseMutationResult<ReopenNegotiationResultDto, Error, ReopenNegotiationInput> {
  const client: NegotiationClient = useNegotiationClient();
  const qc = useQueryClient();
  return useMutation<ReopenNegotiationResultDto, Error, ReopenNegotiationInput>({
    mutationFn: (input) => client.reopen(campaignId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authStateKey(campaignId) });
      // LOT N1 — le read-model par LIGNE reflète le même état serveur : on l'invalide aussi, sinon la
      // page refondue (N4) afficherait un état périmé après une ouverture ou un verrouillage.
      void qc.invalidateQueries({ queryKey: linesKey(campaignId) });
      // LOT N3 — l'historique est une projection du MÊME état serveur : une ouverture ou un verrouillage
      // y ajoute une entrée. On invalide le PRÉFIXE (toutes pages, tous onglets), sinon l'onglet actif
      // afficherait un flux périmé juste après le geste qui vient de l'enrichir.
      void qc.invalidateQueries({ queryKey: [...NEGOTIATION_KEY, 'history', campaignId] });
    },
  });
}

/** VERROUILLER la négociation (verrouillage GLOBAL, DBU l.1266). Invalide l'état NegotiationAuth. */
export function useLockNegotiation(
  campaignId: string,
): UseMutationResult<LockNegotiationResultDto, Error, void> {
  const client: NegotiationClient = useNegotiationClient();
  const qc = useQueryClient();
  return useMutation<LockNegotiationResultDto, Error, void>({
    mutationFn: () => client.lock(campaignId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authStateKey(campaignId) });
      // LOT N1 — le read-model par LIGNE reflète le même état serveur : on l'invalide aussi, sinon la
      // page refondue (N4) afficherait un état périmé après une ouverture ou un verrouillage.
      void qc.invalidateQueries({ queryKey: linesKey(campaignId) });
      // LOT N3 — l'historique est une projection du MÊME état serveur : une ouverture ou un verrouillage
      // y ajoute une entrée. On invalide le PRÉFIXE (toutes pages, tous onglets), sinon l'onglet actif
      // afficherait un flux périmé juste après le geste qui vient de l'enrichir.
      void qc.invalidateQueries({ queryKey: [...NEGOTIATION_KEY, 'history', campaignId] });
    },
  });
}

/**
 * VERROUILLER UN fournisseur (verrouillage INDIVIDUEL, DBU l.1264 — « Verrouiller ce fournisseur »,
 * item 34b). Le back porte la même route que le lock global mais discrimine sur `supplierSiret`.
 * L'argument de mutation est le SIRET ciblé. Invalide l'état NegotiationAuth après succès.
 */
export function useLockNegotiationSupplier(
  campaignId: string,
): UseMutationResult<LockNegotiationResultDto, Error, string> {
  const client: NegotiationClient = useNegotiationClient();
  const qc = useQueryClient();
  return useMutation<LockNegotiationResultDto, Error, string>({
    mutationFn: (supplierSiret) => client.lock(campaignId, supplierSiret),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authStateKey(campaignId) });
      // LOT N1 — le read-model par LIGNE reflète le même état serveur : on l'invalide aussi, sinon la
      // page refondue (N4) afficherait un état périmé après une ouverture ou un verrouillage.
      void qc.invalidateQueries({ queryKey: linesKey(campaignId) });
      // LOT N3 — l'historique est une projection du MÊME état serveur : une ouverture ou un verrouillage
      // y ajoute une entrée. On invalide le PRÉFIXE (toutes pages, tous onglets), sinon l'onglet actif
      // afficherait un flux périmé juste après le geste qui vient de l'enrichir.
      void qc.invalidateQueries({ queryKey: [...NEGOTIATION_KEY, 'history', campaignId] });
    },
  });
}

/**
 * LOT N2 — VERROUILLER UNE SÉLECTION. L'écran coche des LIGNES ; l'argument de mutation est la liste des
 * fournisseurs DISTINCTS de la sélection (le verrouillage reste au grain FOURNISSEUR — précision
 * GATE_HUMAN du 2026-09-18). UN SEUL appel, pour que l'API puisse pré-vérifier le lot entier avant
 * d'écrire : une boucle côté client produirait un lot appliqué à moitié. Après succès, on invalide l'état
 * NegotiationAuth ET le read-model par LIGNE (sans quoi la page refondue afficherait un état périmé).
 */
export function useLockNegotiationSelection(
  campaignId: string,
): UseMutationResult<LockSelectionResultDto, Error, readonly string[]> {
  const client: NegotiationClient = useNegotiationClient();
  const qc = useQueryClient();
  return useMutation<LockSelectionResultDto, Error, readonly string[]>({
    mutationFn: (supplierSirets) => client.lockSelection(campaignId, supplierSirets),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authStateKey(campaignId) });
      void qc.invalidateQueries({ queryKey: linesKey(campaignId) });
      // LOT N3 — l'historique est une projection du MÊME état serveur : une ouverture ou un verrouillage
      // y ajoute une entrée. On invalide le PRÉFIXE (toutes pages, tous onglets), sinon l'onglet actif
      // afficherait un flux périmé juste après le geste qui vient de l'enrichir.
      void qc.invalidateQueries({ queryKey: [...NEGOTIATION_KEY, 'history', campaignId] });
    },
  });
}

/**
 * Comparaisons avant/après de PLUSIEURS fournisseurs (item 34c/34d). Alimente la colonne « Date de
 * modification effective » du tableau de suivi ET les entrées « modification » du journal horodaté,
 * SANS timestamp de modification BC-local (le campaign BC ne possède qu'authorizedAt/lockedAt ; la
 * date de modification effective vit dans le read-model de comparaison, rate-collection). `useQueries`
 * fetch une comparaison par SIRET (petit N = cohorte rouverte finalisée) ; la MÊME `comparisonKey`
 * partage le cache avec le panneau de dépliage (`useNegotiationComparison`) — AUCUN double appel réseau.
 * Retourne une Map SIRET → `dateModifEffective` (ISO 8601) pour les comparaisons chargées.
 */
export function useNegotiationEffectiveModifDates(
  campaignId: string,
  supplierSirets: readonly string[],
): ReadonlyMap<string, string | null> {
  const client: NegotiationClient = useNegotiationClient();
  const results = useQueries({
    queries: supplierSirets.map((supplierSiret) => ({
      queryKey: comparisonKey(campaignId, supplierSiret),
      queryFn: (): Promise<NegotiationComparisonDto> =>
        client.getNegotiationComparison(campaignId, supplierSiret),
    })),
  });
  const map = new Map<string, string | null>();
  results.forEach((result, index) => {
    const siret = supplierSirets[index];
    if (siret !== undefined && result.data !== undefined) {
      map.set(siret, result.data.dateModifEffective);
    }
  });
  return map;
}
